// Панелът „Колони": видимост, плътност, връщане към подразбирането.
//
// Подредбата се мени с влачене в хедъра на таблицата — стрелки тук няма.
// До 10 колони — една колона, над 10 — две колони по равно (16 → 8 + 8). Над
// 20 двете колони растат равномерно; списъкът се вижда цял до 12 реда на
// колона (24 опции), а над тях скролва.

import { useEffect, useRef, useState } from "react";
import { Checkbox, Popover } from "antd";
import { useTranslation } from "react-i18next";
import { ControlOutlined } from "@ant-design/icons";

import { useViewport } from "../../hooks/useViewport";
import type { Density } from "./types";

export type ColumnsPanelItem = {
  id: string;
  label: string;
  visible: boolean;
  canHide: boolean;
};

type Props = {
  items: ColumnsPanelItem[];
  onToggle: (columnId: string, visible: boolean) => void;
  density: Density;
  onDensityChange: (density: Density) => void;
  onReset: () => void;
  hasCustomLayout: boolean;
};

const DENSITIES: Density[] = ["compact", "normal", "roomy"];

/** Над толкова опции списъкът минава на две колони. */
const SINGLE_COLUMN_LIMIT = 10;

export const ColumnsPanel = ({
  items,
  onToggle,
  density,
  onDensityChange,
  onReset,
  hasCustomLayout,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const hiddenCount = items.filter((item) => !item.visible).length;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Две колони само когато не се събират в една и има място за тях — на
  // тесен екран остава една, със скрол. Подредбата е по колони: първата
  // половина отгоре надолу вляво, втората — вдясно, както вървят в таблицата.
  const twoColumns = !isNarrow && items.length > SINGLE_COLUMN_LIMIT;
  const rowsPerColumn = twoColumns ? Math.ceil(items.length / 2) : items.length;
  const columns = twoColumns
    ? [items.slice(0, rowsPerColumn), items.slice(rowsPerColumn)]
    : [items];

  // Ширината следва имената: всяка колона е колкото най-дългото име в нея.
  // Панелът е поне 240px (за „Плътност") и най-много 484px — по-дълго име
  // се реже с „…".
  const content = (
    <div className="flex w-max min-w-[240px] max-w-[484px] flex-col gap-2.5 py-0.5">
      <div className="flex flex-col gap-1.5">
        <p className="font-pos-mono text-[10px] font-bold tracking-[0.1em] text-muted">
          {t("dataTable.density").toUpperCase()}
        </p>
        <div className="flex items-center gap-1 rounded-lg bg-fill p-[3px]">
          {DENSITIES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onDensityChange(option)}
              className={`h-[26px] flex-1 rounded-md text-[11.5px] font-bold transition-colors ${
                density === option
                  ? "bg-surface text-ink shadow-sm"
                  : "text-soft hover:text-ink"
              }`}
            >
              {t(`dataTable.density_${option}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="h-px bg-shade-2" />

      <p className="font-pos-mono text-[10px] font-bold tracking-[0.1em] text-muted">
        {t("dataTable.columns").toUpperCase()}
      </p>
      {/* Ред 30px + 2px разстояние: 12 реда = 382px, над тях — скрол. */}
      <div
        className={`-mx-1 grid max-h-[382px] gap-x-4 overflow-y-auto px-1 ${
          twoColumns
            ? "grid-cols-[minmax(0,auto)_minmax(0,auto)] justify-start"
            : "grid-cols-1"
        }`}
      >
        {columns.map((columnItems, columnIndex) => (
          <ul key={columnIndex} className="flex min-w-0 flex-col gap-0.5">
            {columnItems.map((item) => (
              <li
                key={item.id}
                className="flex h-[30px] items-center rounded-md px-1.5 hover:bg-shade-1"
              >
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={item.visible}
                    disabled={!item.canHide}
                    onChange={(event) => onToggle(item.id, event.target.checked)}
                  />
                  <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-ink">
                    {item.label}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ))}
      </div>

      {hasCustomLayout ? (
        <button
          type="button"
          onClick={onReset}
          className="self-start text-[11.5px] font-bold text-brand-deep-ink hover:underline"
        >
          {t("dataTable.resetLayout")}
        </button>
      ) : null}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={content}
      arrow={false}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-[12px] font-bold transition-colors ${
          hiddenCount || open
            ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
            : "border-shade-3 bg-surface text-ink hover:bg-fill"
        }`}
      >
        <ControlOutlined className="text-[13px]" />
        {t("dataTable.columns")}
        {hiddenCount ? (
          <span className="font-pos-mono text-[11px] tabular-nums">
            {items.length - hiddenCount}/{items.length}
          </span>
        ) : null}
      </button>
    </Popover>
  );
};
